import { Option } from "@inrev/core";
import { match, P } from "ts-pattern";
import { isNotFoundError } from "../../../utils/error";
import type { PDFViewerControl, useDocumentUrl } from "./documentSource";
import type { DocumentLoadState } from "./useDocumentViewer";

/** Whether the document is on screen yet, for callers that offer actions on it. */
export type DocumentViewerStatus = "loading" | "ready" | "unavailable";

/** What the viewport shows: the pages, or the state that stands in for them. */
export type ViewState =
	| { kind: "no-document" }
	| { kind: "loading" }
	| { kind: "not-found"; message: string }
	| { kind: "url-failed" }
	| { kind: "password"; incorrect: boolean; checking: boolean }
	| { kind: "unreadable" }
	| { kind: "load-failed" }
	| { kind: "ready" };

export const STATUS_BY_VIEW: Record<ViewState["kind"], DocumentViewerStatus> = {
	"no-document": "unavailable",
	loading: "loading",
	"not-found": "unavailable",
	"url-failed": "unavailable",
	password: "loading",
	unreadable: "unavailable",
	"load-failed": "unavailable",
	ready: "ready",
};

export const viewStateOf = ({
	control,
	source,
	load,
	notFoundMessage,
}: {
	control: Option<PDFViewerControl>;
	source: ReturnType<typeof useDocumentUrl>;
	load: DocumentLoadState;
	notFoundMessage: Option<string>;
}): ViewState => {
	if (Option.isNone(control)) return { kind: "no-document" };
	if (source.isLoading) return { kind: "loading" };
	if (Option.isNone(source.url))
		return Option.isSome(notFoundMessage) && isNotFoundError(source.error)
			? { kind: "not-found", message: notFoundMessage }
			: { kind: "url-failed" };
	return match(load)
		.returnType<ViewState>()
		.with({ status: P.union("idle", "loading") }, () => ({ kind: "loading" }))
		.with({ status: "password" }, ({ incorrect, checking }) => ({
			kind: "password",
			incorrect,
			checking,
		}))
		.with({ status: "unreadable" }, () => ({ kind: "unreadable" }))
		.with({ status: "failed" }, () => ({ kind: "load-failed" }))
		.with({ status: "ready" }, () => ({ kind: "ready" }))
		.exhaustive();
};
